/**
 * Exception Workspace API service — calls Runtime /api/v3 routes with mock fallback.
 */
import { apiClient } from './apiClient';
import {
  Signal,
  ExceptionCase,
  TimelineEvent,
  mockSignals,
  mockCases,
  mockTimeline,
} from '../data/mockExceptionData';
import {
  vikMockCases,
  vikMockSignals,
  vikMockTimeline,
} from '../data/mockVASExceptionData';
import {
  scMockCases,
  scMockSignals,
  scMockTimeline,
} from '../data/mockSupplyChainExceptionData';

// Merged mock data sets
const allMockCases = [...mockCases, ...vikMockCases, ...scMockCases];
const allMockTimeline: Record<string, TimelineEvent[]> = { ...mockTimeline, ...vikMockTimeline, ...scMockTimeline };

export interface CaseListResponse {
  cases: ExceptionCase[];
  total: number;
}

export interface TimelineResponse {
  case_id: string;
  events: TimelineEvent[];
  total: number;
}

export interface InsightSummary {
  case_id: string;
  traces: number;
  evidence: number;
  decisions: number;
  actions: number;
  outcomes: number;
  context_graph: { nodes: number; edges: number };
  _fallback?: boolean;
}

async function fetchSignals(): Promise<Signal[]> {
  try {
    const data = await apiClient.get('/v3/signals');
    if (Array.isArray(data) && data.length > 0) return data;
  } catch { /* fallback */ }
  return [...mockSignals, ...vikMockSignals, ...scMockSignals];
}

async function fetchCases(params?: { status?: string; domain?: string }): Promise<CaseListResponse> {
  try {
    const data = await apiClient.get('/v3/exception-cases', { params: params || {} });
    if (data && Array.isArray(data.cases) && data.cases.length > 0) return data;
  } catch { /* fallback */ }
  let cases = allMockCases;
  if (params?.status) cases = cases.filter(c => c.status === params.status);
  if (params?.domain) cases = cases.filter(c => c.domain === params.domain);
  return { cases, total: cases.length };
}

async function fetchCase(caseId: string): Promise<ExceptionCase | null> {
  try {
    const data = await apiClient.get(`/v3/exception-cases/${caseId}`);
    if (data && data.case_id) return data;
  } catch { /* fallback */ }
  return allMockCases.find(c => c.case_id === caseId) || null;
}

async function fetchTimeline(caseId: string): Promise<TimelineResponse> {
  try {
    const data = await apiClient.get(`/v3/exception-cases/${caseId}/timeline`);
    if (data && Array.isArray(data.events) && data.events.length > 0) return data;
  } catch { /* fallback */ }
  const events = allMockTimeline[caseId] || [];
  return { case_id: caseId, events, total: events.length };
}

async function fetchInsights(caseId: string): Promise<InsightSummary> {
  try {
    const data = await apiClient.get(`/v3/exception-cases/${caseId}/insights`);
    if (data && !data._fallback) return data;
  } catch { /* fallback */ }
  const events = allMockTimeline[caseId] || [];
  return {
    case_id: caseId,
    traces: events.filter(e => e.type === 'agent_trace').length,
    evidence: events.filter(e => e.type === 'evidence_collected').length,
    decisions: events.filter(e => e.type === 'decision').length,
    actions: events.filter(e => e.type === 'action').length,
    outcomes: events.filter(e => e.type === 'outcome').length,
    context_graph: { nodes: 0, edges: 0 },
    _fallback: true,
  };
}

async function createCaseFromSignal(signalId: string): Promise<ExceptionCase | null> {
  try {
    const data = await apiClient.post(`/v3/signals/${signalId}/create_case`);
    if (data && data.case_id) return data;
  } catch { /* fallback */ }
  return null;
}

async function startWorkflow(caseId: string): Promise<{ case_id: string; workflow_id: string; started: boolean }> {
  try {
    const data = await apiClient.post(`/v3/exception-cases/${caseId}/start_workflow`);
    if (data) return data;
  } catch { /* fallback */ }
  return { case_id: caseId, workflow_id: 'mock-workflow', started: false };
}

export const exceptionService = {
  fetchSignals,
  fetchCases,
  fetchCase,
  fetchTimeline,
  fetchInsights,
  createCaseFromSignal,
  startWorkflow,
};
